/* sesion.tsx — quién está usando la aplicación y qué puede hacer.
 *
 * Sustituye a `app/ui/comun/session.js`, que además de cargar `/api/me` reemplazaba `fetch` para
 * redirigir al ingreso ante un 401. Aquí eso NO se hace desde la capa de red: `cliente.ts` lanza
 * un `ErrorApi` y quien decide navegar es el router. Un `location.href` dentro de un `fetch`
 * hace imposible mostrar «tu sesión venció, vuelve a entrar» sin perder lo que estabas viendo.
 *
 * Ocultar en la interfaz lo que la cuenta no puede usar es COMODIDAD, no seguridad: cada
 * endpoint verifica igual. Sin esto la pantalla mostraría botones que siempre responden 403.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { ErrorApi, obtener } from '../api/cliente'
import type { Capacidad, Yo } from '../api/tipos'

interface EstadoSesion {
  yo: Yo | null
  cargando: boolean
  /** Falla que no es «no hay sesión»: red caída, CORS mal, servidor en mantenimiento. */
  error: string | null
  /** ¿La cuenta tiene esta capacidad? Falso mientras carga. */
  puede: (cap: Capacidad) => boolean
  recargar: () => Promise<void>
}

const Contexto = createContext<EstadoSesion | null>(null)

export function ProveedorSesion({ children }: { children: ReactNode }) {
  const [yo, setYo] = useState<Yo | null>(null)
  const [cargando, setCargando] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const recargar = useCallback(async () => {
    setCargando(true)
    try {
      setYo(await obtener<Yo>('/api/me'))
      setError(null)
    } catch (e) {
      setYo(null)
      // Un 401 no es un error que mostrar: es el estado normal de quien todavía no ha entrado.
      setError(e instanceof ErrorApi && e.sinSesion ? null : (e as Error).message)
    } finally {
      setCargando(false)
    }
  }, [])

  useEffect(() => {
    void recargar()
  }, [recargar])

  const valor = useMemo<EstadoSesion>(
    () => ({
      yo,
      cargando,
      error,
      puede: (cap) => Boolean(yo?.caps?.[cap]),
      recargar,
    }),
    [yo, cargando, error, recargar],
  )

  return <Contexto.Provider value={valor}>{children}</Contexto.Provider>
}

export function useSesion(): EstadoSesion {
  const v = useContext(Contexto)
  if (!v) throw new Error('useSesion fuera de <ProveedorSesion>')
  return v
}
